import gql from 'graphql-tag';
// Import the composable we just created
import { useEntryPage } from '@/composables/useEntryPage';

// GraphQL fragments are still needed for the original mixin
export const SeomaticFragment = gql`
  fragment SeomaticFragment on SeomaticInterface {
    metaTagContainer
    metaTitleContainer
    metaJsonLdContainer
    metaLinkContainer
  }
`;

export const EntryPageFragment = gql`
  fragment EntryPageFragment on EntryInterface {
    typeHandle
    sectionHandle

    localized(status: "live") {
      uri
      language
    }
    id
    title
    uri
    parent {
      title
      uri
      slug
      parent {
        title
        uri
        slug
      }
    }
    ... on workoutCollection_Entry {
      id
      premiumContentRestrictions {
        ... on premiumContentRestrictions_MatrixField {
          ... on settings_Entry {
            id
            authenticatedOnly
            purchasedProductOnly
          }
        }
      }
    }
  }
`;

export const ShopHomeBreadcrumbFragment = gql`
  fragment ShopHomeBreadcrumbFragment on Query {
    shopHomeEntry: entry(section: "shop", site: [$site], level: 1) {
      title
      uri
    }
  }
`;

// The mixin now uses the composable internally
export default {
  scrollToTop: true,

  data() {
    return {
      seomatic: null,
      entry: null,
      isRestricted: false,
      breadCrumbs: [],
      // Store the composable instances
      _entryPageComposable: null,
    };
  },

  created() {
    // Initialize the composable
    this._entryPageComposable = useEntryPage(this.entry);

    // Standard setup
    this.setSectionHandle();
    this.setAlternates();
    this.resetNewsletterBlocks();

    this.isRestricted = this.entry?.premiumContentRestrictions?.length > 0;
  },

  mounted() {
    this.checkPremiumContent();
  },

  methods: {
    // Use the composable for these methods
    checkPremiumContent() {
      // Update the entry in the composable if needed
      if (this.entry !== this._entryPageComposable.entry) {
        this._entryPageComposable.setEntry(this.entry);
      }

      // Call the composable method
      this._entryPageComposable.checkPremiumContent();
    },

    gtmPushPageMeta(options, preventUserUpdate = false) {
      // Ensure the composable is initialized
      if (!this._entryPageComposable) {
        this._entryPageComposable = useEntryPage(this.entry);
      }

      // Use the composable method
      this._entryPageComposable.gtmPushPageMeta(options, preventUserUpdate);
    },

    countOrdersTotal() {
      return this._entryPageComposable?.countOrdersTotal() || 0;
    },

    getUserCountry() {
      return this._entryPageComposable?.getUserCountry();
    },

    gtmPushUserMeta() {
      this._entryPageComposable?.gtmPushUserMeta();
    },

    metaInfoTitle() {
      let curr = this;
      let title = null;
      do {
        title = curr?.$metaInfo?.title;
        curr = curr.$parent;
      } while (!title && curr);
      return title;
    },

    // Methods that don't need the composable
    resetNewsletterBlocks() {
      this.$store.commit('newsletterBlocks/reset');
    },

    setSectionHandle() {
      this.$store.dispatch('page/setSectionHandle', this.entry?.sectionHandle);
    },

    /**
     * builds breadcrumb from parents property recursively - skips for root-entry with uri: ''
     * @returns {array} - shape list of objects {label: 'title' , route: {path: '/some/uri'}}
     */
    buildBreadCrumb() {
      // Initialize the composable if needed
      if (!this._entryPageComposable) {
        this._entryPageComposable = useEntryPage(this.entry);
      }

      return this._entryPageComposable.buildBreadCrumb();
    },

    setChildBreadCrumb(crumbs) {
      // Initialize the composable if needed
      if (!this._entryPageComposable) {
        this._entryPageComposable = useEntryPage(this.entry);
      }

      this._entryPageComposable.setChildBreadCrumb(crumbs);
    },

    setAlternates() {
      this.$store.commit('mainNav/SET_ALTERNATES', this.alternates);
    },
  },

  computed: {
    alternates() {
      const localized =
        this.entry?.localized?.reduce((acc, curr) => {
          if (curr.language === 'gsw-CH') {
            curr.language = 'ch-de';
          } else if (curr.language === 'gsw-FR') {
            curr.language = 'ch-fr';
          }

          acc[curr.language] = this.$localeRootPath(curr.uri, curr.language);
          return acc;
        }, {}) || {};

      return {
        [this.$i18n.locale]: this.$route.path,
        ...localized,
      };
    },
  },
};
